import React, {useRef, useEffect, useState} from 'react';
import mapboxgl from 'mapbox-gl';
import Legend from './Legend';
import Optionsfield from './Optionsfield';
import './Map.css';

// The following is required to stop "npm build" from transpiling mapbox code.
// notice the exclamation point in the import.
// @ts-ignore
// eslint-disable-next-line import/no-webpack-loader-syntax, import/no-unresolved
mapboxgl.workerClass = require('worker-loader!mapbox-gl/dist/mapbox-gl-csp-worker').default;

mapboxgl.accessToken =
    'pk.eyJ1IjoibnRob21hczA4MCIsImEiOiJja3hmaG85OXcwNGxuMndtYXIyMmZ4a3RvIn0.8D8wEzr8RPis8iw06_AFCA';

const Map = () => {
        const options = [
            {
                name: 'Isolation',
                description: 'Degree to which the racial composition of place average Brown person lives in varies from the actual racial composition of the greater surrounding area',
                property: 'Isolation',
                stops: [
                    [0, '#F2F12D'],
                    [0.05, '#E6B71E'],
                    [0.1, '#DA9C20'],
                    [0.15, '#CA8323'],
                    [0.2, '#B86B25'],
                    [0.25, '#A25626'],
                    [0.3, '#9a4f32'],
                    [0.35, '#8f340f'],
                    [0.4, '#651c00'],
                    [0.45, '#4b1806']
                ]
            },
            {
                name: 'Divergence',
                description: 'Degree to which the demographics of each particular sub geography vary from the demographics of the larger area',
                property: 'Divergence',
                stops: [
                    [0, '#F2F12D'],
                    [0.05, '#E6B71E'],
                    [0.1, '#DA9C20'],
                    [0.15, '#CA8323'],
                    [0.2, '#B86B25'],
                    [0.25, '#A25626'],
                    [0.3, '#9a4f32'],
                    [0.35, '#8f340f'],
                    [0.4, '#651c00'],
                    [0.45, '#4b1806']
                ]
            },
            {
                name: 'Composite Segregation',
                description: 'An average of the above two mentioned indexes to cover the shortcomings of both',
                property: 'AverageSegregation',
                stops: [
                    [0, '#F2F12D'],
                    [0.05, '#E6B71E'],
                    [0.1, '#DA9C20'],
                    [0.15, '#CA8323'],
                    [0.2, '#B86B25'],
                    [0.25, '#A25626'],
                    [0.3, '#9a4f32'],
                    [0.35, '#8f340f'],
                    [0.4, '#651c00'],
                    [0.45, '#4b1806']
                ]
            }
        ];

        const mapContainerRef = useRef(null);
        const [active, setActive] = useState(options[0]);
        const [map, setMap] = useState(null);

// Initialize map when component mounts
        useEffect(() => {
            const map = new mapboxgl.Map({
                container: mapContainerRef.current,
                style: 'mapbox://styles/mapbox/streets-v11',
                center: [-98, 38.88],
                zoom: 3
            });

            map.on('load', () => {

                map.addSource('tracts', {
                    'type': 'vector',
                    'url': 'mapbox://nthomas080.tl_2020_us_tract-FINAL'
                });

                map.addSource('counties', {
                    'type': 'vector',
                    'url': 'mapbox://nthomas080.b3lysq15'
                });

                map.addSource('states', {
                    'type': 'vector',
                    'url': 'mapbox://nthomas080.7f7so0a1'
                });

                map.addLayer(
                    {
                        'id': 'states',
                        'source': 'states',
                        'source-layer': 'tl_2020_us_state-FINAL-bffxgb',
                        'type': 'fill',
                        'maxzoom': 7,
                        'layout': {}
                    }
                );

                map.addLayer(
                    {
                        'id': 'counties',
                        'source': 'counties',
                        'source-layer': 'tl_2020_us_county-FINAL-dzo24t',
                        'minzoom': 7,
                        'maxzoom': 10,
                        'type': 'fill',
                        'layout': {}
                    }
                );

                map.addLayer(
                    {
                        'id': 'tracts',
                        'source': 'tracts',
                        'source-layer': 'tracts-FINAL',
                        'minzoom': 10,
                        'type': 'fill',
                        'layout': {},
                        'paint': {
                            'fill-color': [
                                'interpolate',
                                ['linear'],
                                ["/", ["get", "BROWN"], ["get", "POP"]],
                                0,
                                '#F2F12D',
                                0.055,
                                '#EED322',
                                0.11,
                                '#E6B71E',
                                0.165,
                                '#DA9C20',
                                0.22,
                                '#CA8323',
                                0.275,
                                '#B86B25',
                                0.33,
                                '#A25626',
                                0.385,
                                '#9a4f32',
                                0.44,
                                '#8f340f',
                                0.495,
                                '#651c00',
                                0.55,
                                '#4b1806'
                            ]
                        }
                    });

                map.addLayer(
                    {
                        'id': 'tract-outline',
                        'source': 'tracts',
                        'source-layer': 'tracts-FINAL',
                        'minzoom': 10,
                        'type': 'line',
                        'layout': {},
                        'paint': {
                            'line-color': '#000',
                            'line-width': 0.4
                        }
                    }
                );

                map.addLayer(
                    {
                        'id': 'county-outline',
                        'source': 'counties',
                        'source-layer': 'tl_2020_us_county-FINAL-dzo24t',
                        'minzoom': 7,
                        'type': 'line',
                        'layout': {},
                        'paint': {
                            'line-color': '#000',
                            'line-width': 0.6
                        }
                    }
                );

                map.addLayer(
                    {
                        'id': 'state-outline',
                        'source': 'states',
                        'source-layer': 'tl_2020_us_state-FINAL-bffxgb',
                        'type': 'line',
                        'layout': {},
                        'paint': {
                            'line-color': '#000',
                            'line-width': 1.2
                        }
                    }
                );

                map.setPaintProperty('states', 'fill-color', {
                    property: active.property,
                    stops: active.stops
                });
                map.setPaintProperty('counties', 'fill-color', {
                    property: active.property,
                    stops: active.stops
                });

                map.setPaintProperty('states', 'fill-opacity', 0.8)
                map.setPaintProperty('counties', 'fill-opacity', 0.8)
                map.setPaintProperty('tracts', 'fill-opacity', 0.8)

                map.on('click', 'states', (e) => {
                    new mapboxgl.Popup()
                        .setLngLat(e.lngLat)
                        .setHTML(
                            `<div> \
                                <h1>Brown Percentage: ${parseFloat(e.features[0].properties.BROWN.toFixed(0)/e.features[0].properties.POP.toFixed(0)*100).toFixed(2)}%</h1> \
                                <h1>Total Population: ${e.features[0].properties.POP.toFixed(0)}</h1> \
                                <h1>Isolation: ${e.features[0].properties.Isolation.toFixed(2)}</h1> \
                                <h1>Divergence: ${e.features[0].properties.Divergence.toFixed(2)}</h1> \
                                <h1>Composite Segregation: ${e.features[0].properties.AverageSegregation.toFixed(2)}</h1> \
                            </div>`)
                        .addTo(map);
                });

                map.on('click', 'counties', (e) => {
                    new mapboxgl.Popup()
                        .setLngLat(e.lngLat)
                        .setHTML(
                            `<div> \
                                <h1>Brown Percentage: ${parseFloat(e.features[0].properties.BROWN.toFixed(0)/e.features[0].properties.POP.toFixed(0)*100).toFixed(2)}%</h1> \
                                <h1>Total Population: ${e.features[0].properties.POP.toFixed(0)}</h1> \
                                <h1>Isolation: ${e.features[0].properties.Isolation.toFixed(2)}</h1> \
                                <h1>Divergence: ${e.features[0].properties.Divergence.toFixed(2)}</h1> \
                                <h1>Composite Segregation: ${e.features[0].properties.AverageSegregation.toFixed(2)}</h1> \
                            </div>`)
                        .addTo(map);
                });

                map.on('click', 'tracts', (e) => {
                    new mapboxgl.Popup()
                        .setLngLat(e.lngLat)
                        .setHTML(
                            `<div> \
                                <h1>Brown Percentage: ${parseFloat(e.features[0].properties.BROWN.toFixed(0)/e.features[0].properties.POP.toFixed(0)*100).toFixed(2)}%</h1> \
                                <h1>Total Population: ${e.features[0].properties.POP.toFixed(0)}</h1> 
                                </div>`)
                        .addTo(map);
                });

                map.on('mouseenter', 'states-layer', () => {
                    map.getCanvas().style.cursor = 'pointer';
                });

                map.on('mouseleave', 'states-layer', () => {
                    map.getCanvas().style.cursor = '';
                });

                setMap(map);
            });
            // Clean up on unmount
            return () => map.remove();
        }, []);

        useEffect(() => {
            paint();
        }, [active]);

        const paint = () => {
            if (map) {
                map.setPaintProperty('states', 'fill-color', {
                    property: active.property,
                    stops: active.stops
                });
                map.setPaintProperty('counties', 'fill-color', {
                    property: active.property,
                    stops: active.stops
                });
            }
        };

        const changeState = i => {
            setActive(options[i]);
            map.setPaintProperty('states', 'fill-color', {
                property: active.property,
                stops: active.stops
            });
            map.setPaintProperty('counties', 'fill-color', {
                property: active.property,
                stops: active.stops
            });
        };

        return (
            <div>
                <div ref={mapContainerRef} className='map-container'/>
                <Legend active={active} stops={active.stops}/>
                <Optionsfield
                    options={options}
                    property={active.property}
                    changeState={changeState}
                />
            </div>
        );
    }
;

export default Map;
